import { Component, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

// Spartan UI Imports - Local paths
import { HlmButtonDirective } from '@shared/spartan/ui-button-helm/src';
import { HlmSpinnerComponent } from '@shared/spartan/ui-spinner-helm/src';
import { HlmAvatarImports } from '@shared/spartan/ui-avatar-helm/src';

// Lucide Icons
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideSearch,
  lucideCheck,
  lucidePlus,
} from '@ng-icons/lucide';

// Organization Feature Imports
import { OrganizationStore } from '@features/organizations/store/organization.store';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { OrganizationWithStats } from '@features/organizations/models/organization.model';
import { HlmSkeletonComponent } from '@spartan-ng/helm/skeleton';

@Component({
  selector: 'app-organization-selector',
  standalone: true,
  host: { '[class.menu-open]': 'isOpen()' },
  imports: [
    CommonModule,
    HlmButtonDirective,
    HlmSpinnerComponent,
    ...HlmAvatarImports,
    NgIconComponent,
  ],
  providers: [
    provideIcons({
      lucideSearch,
      lucideCheck,
      lucidePlus,
    }),
  ],
  templateUrl: './organization-selector.html',
  styleUrls: ['./organization-selector.css'],
})
export class OrganizationSelectorComponent {
  // Dependency Injection
  private organizationStore = inject(OrganizationStore);
  private currentOrgStore = inject(CurrentOrganizationStore);
  private router = inject(Router);

  // Component State
  isOpen = signal(false);
  searchQuery = signal('');
  private hasAttemptedLoad = signal(false); // Track if we've tried loading

  // Store Computed Properties
  organizations = this.organizationStore.organizations;
  isLoading = this.organizationStore.isLoading;
  currentOrganization = this.currentOrgStore.organization;
  currentAccess = this.currentOrgStore.access;

  // Filtered Organizations based on search
  filteredOrganizations = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const orgs = this.organizations();

    if (!orgs || !Array.isArray(orgs)) {
      return [];
    }

    if (!query) return orgs;

    return orgs.filter(
      (org: OrganizationWithStats) =>
        org.name.toLowerCase().includes(query) ||
        org.slug?.toLowerCase().includes(query)
    );
  });

  visibleOrganizations = computed(() => this.filteredOrganizations().slice(0, 5));

  // Current organization initials for avatar
  currentOrgInitials = computed(() => {
    const org = this.currentOrganization();
    if (!org || !org.name) return '?';

    return org.name
      .split(' ')
      .map((word: string) => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  });

  currentOrgColor = computed(() => this.avatarColor(this.currentOrganization()));

  constructor() {
    // Load organizations once when component initializes
    this.loadOrganizationsIfNeeded();

    // Note: We do NOT auto-select organization here anymore
    // The organization guard handles initial selection
    // This prevents race conditions between guard and component
  }

  private async loadOrganizationsIfNeeded(): Promise<void> {
    const orgs = this.organizations();
    const loading = this.isLoading();
    const hasAttempted = this.hasAttemptedLoad();

    // Only load if we haven't attempted before and we're not already loading
    if (!hasAttempted && !loading) {
      this.hasAttemptedLoad.set(true);
      try {
        await this.organizationStore.loadOrganizations();
      } catch (error) {
        console.error('Failed to load organizations:', error);
        // Don't reset hasAttemptedLoad on error to avoid infinite retries
        // User can manually retry if needed
      }
    }
  }

  // Dropdown Control Methods
  toggleDropdown(): void {
    this.isOpen.update((open) => !open);
    if (this.isOpen()) {
      this.searchQuery.set('');
    }
  }

  closeDropdown(): void {
    this.isOpen.set(false);
    this.searchQuery.set('');
  }

  // Search Methods
  onSearchChange(query: string): void {
    this.searchQuery.set(query);
  }

  // Organization Selection
  async selectOrganization(organization: OrganizationWithStats): Promise<void> {
    try {
      // Set current organization without updating URL (we'll navigate instead)
      this.currentOrgStore.setCurrentOrganization(organization, false);
      this.closeDropdown();

      // Navigate to the dashboard of the selected organization
      await this.router.navigate([
        '/organizations',
        organization.slug,
        'dashboard',
      ]);
    } catch (error) {
      console.error('Failed to select organization:', error);
      // TODO: Show error toast/notification
    }
  }

  // Organization Actions
  createOrganization(): void {
    this.closeDropdown();
    this.router.navigate(['/organizations/create']);
  }

  navigateToOnboarding(): void {
    this.closeDropdown();
    this.router.navigate(['/organizations']);
  }

  // Manual refresh method for retry scenarios
  async refreshOrganizations(): Promise<void> {
    this.hasAttemptedLoad.set(false);
    await this.loadOrganizationsIfNeeded();
  }

  // Utility Methods
  avatarColor(organization: OrganizationWithStats | null): string {
    const color = organization?.settings?.branding?.colors?.primary;
    return color && /^#[0-9a-f]{6}$/i.test(color) ? color : 'var(--ks-navy)';
  }

  getRoleDisplayText(role?: string | null): string {
    const roleMap: Record<string, string> = {
      owner: 'Inhaber',
      admin: 'Administrator',
      member: 'Mitglied',
      viewer: 'Betrachter',
    };
    return role ? roleMap[role] || 'Zugriff' : 'Zugriff';
  }

}
